import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DevUser } from '@/application/auth/authTypes';
import * as authApi from '@/infrastructure/http/authApi';
import { useAuth } from '@/presentation/contexts/useAuth';

/**
 * Selector de impersonación por rol, visible SOLO en desarrollo.
 * Lista las fixtures del backend (`/api/auth/dev/users`) y, al elegir una, inicia
 * sesión como ese usuario sin password. En producción el endpoint no existe → no se muestra.
 */
export function DevImpersonation() {
  const { t } = useTranslation();
  const { impersonate } = useAuth();
  const [users, setUsers] = useState<DevUser[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    let cancelled = false;
    authApi
      .listDevUsers()
      .then((list) => {
        if (!cancelled) {
          setUsers(list);
        }
      })
      .catch(() => {
        // Endpoint no disponible (prod / AUTH_MODE=sql): simplemente no se muestra.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!import.meta.env.DEV || users.length === 0) {
    return null;
  }

  async function handleImpersonate(username: string) {
    setError('');
    try {
      await impersonate(username);
    } catch {
      setError(t('auth.invalidCredentials'));
    }
  }

  return (
    <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
      <p className="font-semibold">{t('auth.impersonateTitle')}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {users.map((user) => (
          <button
            key={user.username}
            type="button"
            onClick={() => handleImpersonate(user.username)}
            className="rounded-md border border-amber-300 px-2.5 py-1.5 font-semibold text-amber-800 transition hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
          >
            {user.username} · {user.role}
          </button>
        ))}
      </div>
      {error && <p className="mt-2 text-red-600 dark:text-red-300">{error}</p>}
    </div>
  );
}
